import type {
  CubeQuery,
  InstructorLink,
  Link,
  PackageInfo,
  Section,
  Session,
} from "@models/ISchema";
import { compareIds, isPresent } from "./shared";

/** A component row in a league, with occupied block counts keyed by week ID. */
export interface AnnualScheduleRow {
  id: string;
  label: string;
  counts: Map<string, number>;
}

/**
 * A package league displayed as a MatrixTable group.
 *
 * @remarks A package `Link` is a league here. Its `code` is the league code shown in
 * both the matrix and drawer; this view model is not a backend `Group` entity.
 */
export interface AnnualScheduleGroup {
  id: string;
  code: string;
  course: Link["course"];
  teachers: InstructorLink[];
  rows: AnnualScheduleRow[];
}

/**
 * Selects unique teachers for a league, preferring their primary assignment.
 *
 * @param assignments - Teacher assignments on the package link.
 * @returns Primary teachers first, with numeric ID order breaking ties.
 */
export const getLeagueTeachers = (assignments: Link["instructors"]): InstructorLink[] => {
  const teachers = new Map<string, InstructorLink>();

  assignments?.forEach(assignment => {
    if (!assignment?.instructor) return;

    const previous = teachers.get(assignment.instructor.id);
    if (!previous || assignment.isPrimary) teachers.set(assignment.instructor.id, assignment);
  });

  return [...teachers.values()].sort(
    (a, b) =>
      Number(!!b.isPrimary) - Number(!!a.isPrimary) || compareIds(a.instructor.id, b.instructor.id),
  );
};

/**
 * Counts distinct occupied blocks for each week of a component.
 *
 * @param sessions - Sessions linked to the component.
 * @returns Block counts keyed by week ID; each session/week/block combination counts once.
 */
export const getWeeklyBlockCounts = (sessions: Section["sessions"]): Map<string, number> => {
  const blocks = new Map<string, Set<string>>();

  sessions?.forEach(session =>
    session.events?.forEach(event => {
      if (!event.block || !event.interval) return;

      const week = blocks.get(event.interval.id) ?? new Set<string>();
      week.add(JSON.stringify([session.id, event.block.id]));
      blocks.set(event.interval.id, week);
    }),
  );

  return new Map([...blocks].map(([week, occupied]) => [week, occupied.size]));
};

/**
 * Joins a league's configured components with their calendar sessions.
 *
 * @param league - Package link containing the configured component sections.
 * @param calendarSections - Sections with scheduled sessions, keyed by section ID.
 * @returns Sorted component rows, including components without calendar sessions.
 */
const getComponentRows = (
  league: Link,
  calendarSections: Map<string, Section>,
): AnnualScheduleRow[] => {
  const components = new Map<
    string,
    { component: NonNullable<Section["component"]>; sessions: Map<string, Session> }
  >();

  league.sections?.forEach(configuredSection => {
    const component = configuredSection.component;
    if (!component) return;

    const row = components.get(component.id) ?? {
      component,
      sessions: new Map<string, Session>(),
    };

    calendarSections
      .get(configuredSection.id)
      ?.sessions?.forEach(session => row.sessions.set(session.id, session));

    components.set(component.id, row);
  });

  return [...components.values()]
    .map(({ component, sessions }) => ({
      id: component.id,
      label: component.name || component.code || component.id,
      counts: getWeeklyBlockCounts([...sessions.values()]),
    }))
    .sort((a, b) => compareIds(a.id, b.id));
};

/**
 * Joins package leagues and calendar sections into matrix groups.
 *
 * @param cube - Shared package response containing links, sections, and sessions.
 * @returns Leagues sorted by course and league code, with their component rows.
 *
 * @remarks Configured components remain visible with zero counts. Sessions are
 * deduplicated within each component before calculating weekly counts. A package
 * `Link` represents one league; `Link.code` is the displayed league code.
 */
export const getAnnualGroups = (cube: CubeQuery | null | undefined): AnnualScheduleGroup[] => {
  const info = cube?.editorView?.info;
  const packageData = info?.__typename === "PackageInfo" ? (info as PackageInfo).package : null;
  const calendarSections = new Map(
    cube?.editorView?.sections?.filter(isPresent).map(section => [section.id, section]) ?? [],
  );

  return (packageData?.links ?? [])
    .filter(isPresent)
    .map(link => ({
      id: link.id,
      code: link.code ?? "",
      course: link.course,
      teachers: getLeagueTeachers(link.instructors),
      rows: getComponentRows(link, calendarSections),
    }))
    .filter(group => group.rows.length > 0)
    .sort(
      (a, b) =>
        compareIds(a.course?.code ?? "", b.course?.code ?? "") ||
        compareIds(a.code, b.code) ||
        compareIds(a.id, b.id),
    );
};
